import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddCluster() {
  const [clusterName, setClusterName] = useState("");
  const [clusterDescription, setClusterDescription] = useState("");
  const [organizationsData, setOrganizationsData] = useState([]); // merged data

  const [selectedOrgs, setSelectedOrgs] = useState([{ orgId: "" }]);

  // Fetch organizations with codes
  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/organizations`);
      return response.data.organizations;
    } catch (error) {
      console.error("Error fetching organizations:", error);
      toast.error("Error fetching organizations.");
      return [];
    }
  };

  // Fetch organization locations
  const fetchOrganizationLocations = async () => {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please login.");
        return [];
      }

      const response = await axios.get(
        `${API_BASE_URL}/organizations/organizationLocations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.organizations;
    } catch (error) {
      console.error("Error fetching organization locations:", error);
      toast.error("Error fetching organization locations.");
      return [];
    }
  };

  // Fetch both and merge on _id
  const fetchAndMergeData = async () => {
    const orgList = await fetchOrganizations();
    const orgLocs = await fetchOrganizationLocations();

    // Merge by _id
    const merged = orgList.map((org) => {
      const locObj = orgLocs.find((loc) => loc._id === org._id);
      return {
        ...org,
        locations: locObj?.locations || [], // add locations array or empty if missing
      };
    });

    setOrganizationsData(merged);
  };

  useEffect(() => {
    fetchAndMergeData();
  }, []);

  const handleOrgChange = (index, orgId) => {
    setSelectedOrgs((prev) =>
      prev.map((item, i) => (i === index ? { orgId } : item))
    );

    const org = organizationsData.find((org) => org._id === orgId);
    // console.log("Selected organization:", org);
  };

  const addOrgRow = () => {
    setSelectedOrgs((prev) => [...prev, { orgId: "" }]);
  };

  const removeOrgRow = (index) => {
    setSelectedOrgs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddCluster = async () => {
    if (!clusterName.trim()) {
      toast.error("Cluster name is required.");
      return;
    }
    if (!clusterDescription.trim()) {
      toast.error("Cluster description is required.");
      return;
    }
    if (
      selectedOrgs.length === 0 ||
      selectedOrgs.some((item) => !item.orgId.trim())
    ) {
      toast.error("Please select all organizations.");
      return;
    }

    const organizations = selectedOrgs.map((item) => item.orgId);

    const formData = {
      cluster_name: clusterName,
      cluster_description: clusterDescription,
      organizations,
    };

    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please login.");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/clusters/add`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(response.data.message || "Cluster created successfully.");
      setClusterName("");
      setClusterDescription("");
      setSelectedOrgs([{ orgId: "" }]);
    } catch (error) {
      console.error("Error creating cluster:", error);
      toast.error("Error creating cluster. Please try again.");
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Add Cluster</h2>

        {/* Cluster Name */}
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Cluster Name</label>
          <input
            type="text"
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Enter cluster name"
            value={clusterName}
            onChange={(e) => setClusterName(e.target.value)}
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Description</label>
          <textarea
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Enter cluster description"
            value={clusterDescription}
            onChange={(e) => setClusterDescription(e.target.value)}
          />
        </div>

        {/* Organizations Section */}
        <div>
          <label className="block text-gray-700 mb-2 text-lg font-semibold">
            Organizations
          </label>

          {/* Header Row */}
          <div className="grid grid-cols-12 gap-4 font-semibold text-gray-800 mb-2">
            <div className="col-span-5">Select Organization</div>
            <div className="col-span-4">Locations</div>
            <div className="col-span-2">Organization Code</div>
            <div className="col-span-1">Action</div>
          </div>

          {/* Dynamic Rows */}
          {selectedOrgs.map((item, index) => {
            const selectedOrg = organizationsData.find(
              (org) => org._id === item.orgId
            );

            const locationsText =
              selectedOrg?.locations?.length > 0
                ? selectedOrg.locations.map((loc) => loc.location_name).join(", ")
                : "No locations available";

            return (
              <div
                key={index}
                className="grid grid-cols-12 gap-4 items-center mb-4"
              >
                {/* Organization Dropdown */}
                <div className="col-span-5">
                  <select
                    className="w-full p-2 border rounded text-gray-900"
                    value={item.orgId}
                    onChange={(e) => handleOrgChange(index, e.target.value)}
                  >
                    <option value="" disabled className="text-gray-400">
                      --- Select Organization ---
                    </option>
                    {organizationsData.map((org) => (
                      <option
                        key={org._id}
                        value={org._id}
                        disabled={selectedOrgs.some(
                          (sel, i) => sel.orgId === org._id && i !== index
                        )}
                      >
                        {org.organization_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Locations text */}
                <div className="col-span-4 text-gray-700">{locationsText}</div>

                {/* Organization Code */}
                <div className="col-span-2 text-gray-700">
                  {selectedOrg?.organization_code || ""}
                </div>

                {/* Remove Button */}
                <div className="col-span-1 text-right">
                  <button
                    onClick={() => removeOrgRow(index)}
                    className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-700"
                    disabled={selectedOrgs.length === 1}
                    title={
                      selectedOrgs.length === 1
                        ? "At least one organization required"
                        : "Remove"
                    }
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add Organization Button */}
          <div className="mt-2">
            <button
              onClick={addOrgRow}
              className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-800"
            >
              Add Organization
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-6 text-right">
          <button
            onClick={handleAddCluster}
            className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300"
          >
            Add Cluster
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddCluster;
